/* 
  Zaktualizowano Canvas2D:
  1. Naprawiono błąd z przesuwaniem tekstu (literówka updateDrawingText -> updateDraggingText).
  2. Zachowano ulepszoną widoczność punktów węzłowych.
*/
import React, { useRef, useEffect, useState } from 'react';
import useSceneStore from '../store/useSceneStore';
import useFurnitureStore from '../store/useFurnitureStore';
import useLogoStore from '../store/useLogoStore';
import useBackgroundStore from '../store/useBackgroundStore';
import useTextStore from '../store/useTextStore';
import useTableStore from '../store/useTableStore';
import useShapeStore from '../store/useShapeStore';
import useLayerStore from '../store/useLayerStore';
import useAuthStore from '../store/useAuthStore';
import useFloorsStore from '../store/useFloorsStore';
import FloorsPanel from '../ui/FloorsPanel';
import LayersPanel from '../ui/LayersPanel';
import HistoryPanel from '../ui/HistoryPanel';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { RiHistoryLine, RiDragMoveFill, RiHome9Line, RiListSettingsLine } from 'react-icons/ri';
import { getSnapPoint, getClosestWall, getPositionOnWall, getWallAngle, pointToLineDistance } from '../core/geometry';
import { GRID_SIZE, SNAP_THRESHOLD, TOOLS, ELEMENT_TYPES, HATCH_PATTERNS } from '../core/constants';
import { FurnitureRenderer } from '../catalog/furnitureRenderer';
import { ShapeRenderer } from '../catalog/shapeRenderer';
import { getItemById } from '../catalog/catalogManager';

const THEME_COLOR = '#1E2937'; 
const THEME_COLOR_LIGHT = '#374151';
const SELECTION_COLOR = '#1E2937';

const Canvas2D = () => {
  const canvasRef = useRef(null);
  const dragOrigin = useRef(null);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const imageCache = useRef({});
  const patternCache = useRef({});
  const [forceUpdate, setForceUpdate] = useState(0);
  const [isPanning, setIsPanning] = useState(false);
  const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 });
  const [lastClickTime, setLastClickTime] = useState(0);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  const { layers } = useLayerStore();
  const planId = useAuthStore((state) => state.planId);
  const { initFloors, togglePanel, isPanelOpen, floors } = useFloorsStore();

  useEffect(() => {
    initFloors();
  }, [initFloors]);

  const {
    tool, scale, position, corners, walls, elements, rooms, surfaces, dimensionLines,
    activeWallId, activeSurfaceId, drawingStartPos, tempEnd, drawing,
    selectedCornerId, selectedWallId, selectedElementId, selectedRoomId,
    selectedSurfaceId, selectedDimensionId, isDraggingCorner, isDraggingWall,
    isDraggingElement, isDraggingSurface, draggingSurfacePointIndex,
    setTool, setStageScale, setStagePosition, startDrawing, updateDrawing, finishDrawing,
    cancelDrawing, startDrawingRoom, updateDrawingRoom, finishDrawingRoom,
    startDrawingSurface, addPointToSurface, finishDrawingSurface,
    startDrawingDimension, updateDrawingDimension, finishDrawingDimension,
    selectCorner, selectWall, selectElement, selectRoom, selectSurface, selectDimension,
    clearSelection, startDraggingCorner, updateDraggingCorner, stopDragging,
    startDraggingWall, updateDraggingWall, startDraggingElement, updateDraggingElement,
    startDraggingSurface: startDraggingSurfaceAction, updateDraggingSurface,
    startDraggingSurfacePoint, updateDraggingSurfacePoint, addElement, deleteElement,
    deleteSurface, deleteWall, deleteRoom, deleteDimension, addNodeToWall, projectName, setProjectName
  } = useSceneStore();

  const {
    furnitureItems, selectedFurnitureId, isDraggingFurniture, addFurnitureItem,
    selectFurnitureItem, clearFurnitureSelection, startDraggingFurniture,
    updateDraggingFurniture, stopDraggingFurniture, removeFurnitureItem, updateFurnitureItem
  } = useFurnitureStore();

  const {
    logoItems, selectedLogoId, isDraggingLogo, selectLogoItem, clearLogoSelection,
    startDraggingLogo, updateDraggingLogo, stopDraggingLogo
  } = useLogoStore();

  const {
    backgroundItem, selectedBackgroundId, isDraggingBackground, selectBackgroundItem,
    clearBackgroundSelection, startDraggingBackground, updateDraggingBackground, stopDraggingBackground
  } = useBackgroundStore();

  const {
    textItems, selectedTextId, isDraggingText, addTextItem, selectTextItem,
    clearTextSelection, startEditingText, startDraggingText, updateDraggingText,
    stopDraggingText, removeTextItem
  } = useTextStore();

  const {
    tables, selectedTableId, isDraggingTable, startDraggingTable, updateDraggingTable,
    stopDraggingTable, selectTable, clearTableSelection, startEditingTable, removeTable
  } = useTableStore();

  const {
    shapeItems, selectedShapeId, isDraggingShape, addShapeItem, selectShapeItem,
    clearShapeSelection, startDraggingShape, updateDraggingShape, stopDraggingShape, removeShapeItem
  } = useShapeStore();

  useEffect(() => {
    if (canvasRef.current) {
      window.exportCanvas = canvasRef.current;
    }
  }, []);

  useEffect(() => {
    const updateSize = () => {
      const container = canvasRef.current?.parentElement;
      if (container) {
        setCanvasSize({
          width: container.offsetWidth,
          height: container.offsetHeight
        });
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Delete') {
        if (selectedFurnitureId) removeFurnitureItem(selectedFurnitureId);
        if (selectedElementId) deleteElement(selectedElementId);
        if (selectedSurfaceId) deleteSurface(selectedSurfaceId);
        if (selectedWallId) deleteWall(selectedWallId);
        if (selectedRoomId) deleteRoom(selectedRoomId);
        if (selectedTextId) removeTextItem(selectedTextId);
        if (selectedTableId) removeTable(selectedTableId);
        if (selectedShapeId) removeShapeItem(selectedShapeId);
        if (selectedDimensionId) deleteDimension(selectedDimensionId);
      }
      if (selectedFurnitureId && !isDraggingFurniture) {
        const item = furnitureItems[selectedFurnitureId];
        if (item) {
          const step = 1;
          let newX = item.x, newY = item.y;
          if (e.key === 'ArrowUp') newY -= step;
          if (e.key === 'ArrowDown') newY += step;
          if (e.key === 'ArrowLeft') newX -= step;
          if (e.key === 'ArrowRight') newX -= step;
          if (newX !== item.x || newY !== item.y) {
            e.preventDefault();
            updateFurnitureItem(selectedFurnitureId, { x: newX, y: newY });
          }
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedFurnitureId, selectedElementId, selectedSurfaceId, selectedWallId, selectedRoomId, selectedTextId, selectedTableId, selectedShapeId, selectedDimensionId, furnitureItems, isDraggingFurniture, removeFurnitureItem, deleteElement, deleteSurface, deleteWall, deleteRoom, removeTextItem, removeTable, removeShapeItem, deleteDimension, updateFurnitureItem]);

  const getImage = (url) => {
    if (!url) return null;
    if (imageCache.current[url]) {
      if (imageCache.current[url].complete) return imageCache.current[url];
      return null;
    }
    const img = new Image();
    img.src = url;
    img.onload = () => setForceUpdate(prev => prev + 1);
    imageCache.current[url] = img;
    return null;
  };

  const getHatchPattern = (ctx, type, color, scaleFactor = 1) => {
    const key = `${type}-${color}-${scaleFactor}`;
    if (patternCache.current[key]) return patternCache.current[key];
    const canvas = document.createElement('canvas');
    const pCtx = canvas.getContext('2d');
    const baseSize = 20;
    const s = baseSize * scaleFactor;
    canvas.width = s; canvas.height = s;
    pCtx.strokeStyle = color; pCtx.lineWidth = 1; pCtx.lineCap = 'square';
    switch (type) {
      case HATCH_PATTERNS.DIAGONAL_45:
        pCtx.beginPath(); pCtx.moveTo(0, s); pCtx.lineTo(s, 0); pCtx.moveTo(-s/2, s/2); pCtx.lineTo(s/2, -s/2); pCtx.moveTo(s/2, s*1.5); pCtx.lineTo(s*1.5, s/2); pCtx.stroke();
        break;
      case HATCH_PATTERNS.DIAGONAL_135:
        pCtx.beginPath(); pCtx.moveTo(0, 0); pCtx.lineTo(s, s); pCtx.moveTo(s/2, -s/2); pCtx.lineTo(s*1.5, s/2); pCtx.moveTo(-s/2, s/2); pCtx.lineTo(s/2, s*1.5); pCtx.stroke();
        break;
      case HATCH_PATTERNS.CROSSHATCH:
        pCtx.beginPath(); pCtx.moveTo(0, s); pCtx.lineTo(s, 0); pCtx.moveTo(-s/2, s/2); pCtx.lineTo(s/2, -s/2); pCtx.moveTo(s/2, s*1.5); pCtx.lineTo(s*1.5, s/2); pCtx.moveTo(0, 0); pCtx.lineTo(s, s); pCtx.moveTo(s/2, -s/2); pCtx.lineTo(s*1.5, s/2); pCtx.moveTo(-s/2, s/2); pCtx.lineTo(s/2, s*1.5); pCtx.stroke();
        break;
      case HATCH_PATTERNS.GRID:
        pCtx.beginPath(); pCtx.moveTo(s/2, 0); pCtx.lineTo(s/2, s); pCtx.moveTo(0, s/2); pCtx.lineTo(s, s/2); pCtx.stroke();
        break;
      case HATCH_PATTERNS.DOTTED:
        pCtx.fillStyle = color; pCtx.beginPath(); pCtx.arc(s/2, s/2, 1.5, 0, Math.PI*2); pCtx.fill();
        break;
      case HATCH_PATTERNS.BRICK:
        pCtx.beginPath(); pCtx.moveTo(0, s/2); pCtx.lineTo(s, s/2); pCtx.moveTo(0, 0); pCtx.lineTo(0, s/2); pCtx.moveTo(s/2, s/2); pCtx.lineTo(s/2, s); pCtx.stroke();
        break;
      case HATCH_PATTERNS.CONCRETE:
        pCtx.fillStyle = color; pCtx.beginPath(); pCtx.arc(s*0.2, s*0.2, 1, 0, Math.PI*2); pCtx.fill(); pCtx.beginPath(); pCtx.arc(s*0.7, s*0.6, 1.5, 0, Math.PI*2); pCtx.fill(); pCtx.beginPath(); pCtx.moveTo(s*0.4, s*0.8); pCtx.lineTo(s*0.5, s*0.9); pCtx.lineTo(s*0.3, s*0.9); pCtx.fill();
        break;
      case HATCH_PATTERNS.SAND:
        pCtx.fillStyle = color; pCtx.beginPath(); pCtx.arc(s*0.2, s*0.2, 1, 0, Math.PI*2); pCtx.fill(); pCtx.beginPath(); pCtx.arc(s*0.8, s*0.6, 1, 0, Math.PI*2); pCtx.fill(); pCtx.beginPath(); pCtx.arc(s*0.2, s*0.8, 1, 0, Math.PI*2); pCtx.fill(); pCtx.beginPath(); pCtx.arc(s*0.8, s*0.2, 1, 0, Math.PI*2); pCtx.fill(); pCtx.beginPath(); pCtx.arc(s*0.5, s*0.5, 1, 0, Math.PI*2); pCtx.fill();
        break;
      case HATCH_PATTERNS.WOOD:
        pCtx.beginPath(); pCtx.moveTo(0, s*0.2); pCtx.bezierCurveTo(s*0.5, s*0.1, s*0.5, s*0.3, s, s*0.2); pCtx.moveTo(0, s*0.5); pCtx.bezierCurveTo(s*0.4, s*0.6, s*0.6, s*0.4, s, s*0.5); pCtx.moveTo(0, s*0.8); pCtx.bezierCurveTo(s*0.3, s*0.9, s*0.7, s*0.7, s, s*0.8); pCtx.stroke();
        break;
      case HATCH_PATTERNS.STEEL:
        pCtx.beginPath(); pCtx.moveTo(0, s); pCtx.lineTo(s, 0); pCtx.moveTo(0, s-4); pCtx.lineTo(s-4, 0); pCtx.stroke();
        break;
      default: break;
    }
    const pattern = ctx.createPattern(canvas, 'repeat');
    patternCache.current[key] = pattern;
    return pattern;
  };

  const screenToWorld = (screenX, screenY) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (screenX - rect.left - position.x) / scale;
    const y = (screenY - rect.top - position.y) / scale;
    return { x, y };
  };

  const findBackgroundAtPosition = (worldPos) => {
    if (!backgroundItem || !layers.background?.isVisible || layers.background?.isLocked) return null;
    const width = backgroundItem.originalWidth * backgroundItem.scale;
    const height = backgroundItem.originalHeight * backgroundItem.scale;
    const dist = Math.sqrt(Math.pow(worldPos.x - backgroundItem.x, 2) + Math.pow(worldPos.y - backgroundItem.y, 2));
    const radius = Math.max(width, height) / 2;
    if (dist <= radius) return { type: 'background', id: backgroundItem.id };
    return null;
  };

  const findLogoAtPosition = (worldPos) => {
    if (!layers.logo?.isVisible || layers.logo?.isLocked) return null;
    const items = Object.values(logoItems).reverse();
    for (const item of items) {
      const width = item.originalWidth * item.scale, height = item.originalHeight * item.scale;
      const dist = Math.sqrt(Math.pow(worldPos.x - item.x, 2) + Math.pow(worldPos.y - item.y, 2));
      const radius = Math.max(width, height) / 2;
      if (dist <= radius) return { type: 'logo', id: item.id };
    }
    return null;
  };

  const findFurnitureAtPosition = (worldPos) => {
    if (!layers.furniture?.isVisible || layers.furniture?.isLocked) return null;
    const items = Object.values(furnitureItems).reverse();
    for (const item of items) {
      const template = getItemById(item.itemId);
      if (!template) continue;
      const currentWidth = item.width !== undefined ? item.width : (template.dimensions.width * item.scale);
      const currentDepth = item.depth !== undefined ? item.depth : (template.dimensions.depth * item.scale);
      const radius = Math.max(currentWidth, currentDepth) / 2;
      const dist = Math.sqrt(Math.pow(worldPos.x - item.x, 2) + Math.pow(worldPos.y - item.y, 2));
      if (dist <= radius) return { type: 'furniture', id: item.id };
    }
    return null;
  };

  const findTextAtPosition = (worldPos, ctx) => {
    if (!layers.texts?.isVisible || layers.texts?.isLocked) return null;
    const items = Object.values(textItems).reverse();
    for (const item of items) {
      ctx.save();
      ctx.font = `${item.fontWeight || 'normal'} ${item.fontSize}px ${item.fontFamily}`;
      const lines = (item.text || '').split('\n');
      let maxWidth = 0;
      lines.forEach(line => {
        const metrics = ctx.measureText(line);
        if (metrics.width > maxWidth) maxWidth = metrics.width;
      });
      const lineHeight = item.fontSize * 1.2, totalHeight = lines.length * lineHeight;
      const dx = worldPos.x - item.x, dy = worldPos.y - item.y;
      const cos = Math.cos(-item.rotation), sin = Math.sin(-item.rotation);
      const localX = dx * cos - dy * sin, localY = dx * sin + dy * cos;
      ctx.restore();
      if (Math.abs(localX) <= maxWidth / 2 && Math.abs(localY) <= totalHeight / 2) return { type: 'text', id: item.id };
    }
    return null;
  };

  const findTableAtPosition = (worldPos, ctx) => {
    const items = Object.values(tables).reverse();
    for (const item of items) {
      const width = item.columnWidth * 2;
      let height = 0; if (item.showTitle) height += item.fontSize + item.padding * 2;
      const rowHeight = item.fontSize + item.padding * 2;
      height += item.data.length * rowHeight;
      if (worldPos.x >= item.x && worldPos.x <= item.x + width && worldPos.y >= item.y && worldPos.y <= item.y + height) return { type: 'table', id: item.id };
    }
    return null;
  };

  const findShapeAtPosition = (worldPos) => {
    if (!layers.shapes?.isVisible || layers.shapes?.isLocked) return null;
    const items = Object.values(shapeItems).reverse();
    for (const item of items) {
      const width = item.dimensions.width * item.scale, height = item.dimensions.height * item.scale;
      const radius = Math.max(width, height) / 2;
      const dist = Math.sqrt(Math.pow(worldPos.x - item.x, 2) + Math.pow(worldPos.y - item.y, 2));
      if (dist <= radius) return { type: 'shape', id: item.id };
    }
    return null;
  };

  const isPointInPolygon = (point, vertices) => {
    let inside = false; const n = vertices.length;
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const pi = vertices[i], pj = vertices[j];
      if (((pi.y > point.y) !== (pj.y > point.y)) && (point.x < (pj.x - pi.x) * (point.y - pi.y) / (pj.y - pi.y) + pi.x)) inside = !inside;
    }
    return inside;
  };

  const findSurfaceAtPosition = (worldPos) => {
    const surfaceIds = Object.keys(surfaces).reverse();
    for (const id of surfaceIds) {
      const surface = surfaces[id];
      if (surface.points.length < 3) continue;
      if (isPointInPolygon(worldPos, surface.points)) return { type: 'surface', id };
    }
    return null;
  };

  const findManualDimensionAtPosition = (worldPos) => {
    if (!layers.manualDimensions?.isVisible) return null;
    const threshold = 10 / scale;
    for (const [id, dim] of Object.entries(dimensionLines)) {
      const { distance } = pointToLineDistance(worldPos, dim.start, dim.end);
      if (distance <= threshold) return { type: 'dimension', id };
    }
    return null;
  };

  const findSurfaceVertexAtPosition = (worldPos, surface) => {
    const threshold = 20 / scale;
    for (let i = 0; i < surface.points.length; i++) {
      const p = surface.points[i];
      const dist = Math.sqrt(Math.pow(worldPos.x - p.x, 2) + Math.pow(worldPos.y - p.y, 2));
      if (dist <= threshold) return i;
    }
    return -1;
  };

  const findRoomAtPosition = (worldPos) => {
    for (const [roomId, room] of Object.entries(rooms)) {
      const roomCorners = room.corners.map(cornerId => corners[cornerId]).filter(Boolean);
      if (roomCorners.length < 3) continue;
      if (isPointInPolygon(worldPos, roomCorners)) return { type: 'room', id: roomId };
    }
    return null;
  };

  const findObjectAtPosition = (worldPos, ctx) => {
    const threshold = 20 / scale;
    const table = findTableAtPosition(worldPos, ctx); if (table) return table;
    const text = findTextAtPosition(worldPos, ctx); if (text) return text;
    const shape = findShapeAtPosition(worldPos); if (shape) return shape;
    const furniture = findFurnitureAtPosition(worldPos); if (furniture) return furniture;
    const logo = findLogoAtPosition(worldPos); if (logo) return logo;
    const dimension = findManualDimensionAtPosition(worldPos); if (dimension) return dimension;
    
    for (const [id, element] of Object.entries(elements)) {
      const dx = worldPos.x - element.position.x;
      const dy = worldPos.y - element.position.y;
      const cos = Math.cos(-element.angle);
      const sin = Math.sin(-element.angle);
      const lx = dx * cos - dy * sin;
      const ly = dx * sin + dy * cos;
      const halfWidth = element.width / 2;
      const depth = element.depth || 15;
      const halfDepth = depth / 2;
      const paddingX = 5 / scale;
      const paddingY = 10 / scale;
      if (Math.abs(lx) <= halfWidth + paddingX && Math.abs(ly) <= halfDepth + paddingY) {
        return { type: 'element', id };
      }
    }

    for (const [id, corner] of Object.entries(corners)) {
      const dist = Math.sqrt(Math.pow(worldPos.x - corner.x, 2) + Math.pow(worldPos.y - corner.y, 2));
      if (dist <= threshold) return { type: 'corner', id };
    }
    
    for (const [id, wall] of Object.entries(walls)) {
      if (!wall.endCornerId) continue;
      const start = corners[wall.startCornerId], end = corners[wall.endCornerId];
      if (!start || !end) continue;
      const { distance } = pointToLineDistance(worldPos, start, end);
      if (distance <= threshold) return { type: 'wall', id };
    }
    
    const surface = findSurfaceAtPosition(worldPos); if (surface) return surface;
    const room = findRoomAtPosition(worldPos); if (room) return room;
    const background = findBackgroundAtPosition(worldPos);
    if (background && tool === 'BACKGROUND') return background;
    if (background && !furniture && !logo && !surface && !text && !table && !shape && tool === TOOLS.SELECT) return background;
    return null;
  };

  const isDoubleClick = (currentTime) => {
    const timeDiff = currentTime - lastClickTime;
    setLastClickTime(currentTime);
    return timeDiff < 300;
  };

  const getDashArrayForStyle = (style) => {
    switch (style) {
      case 'dashed': return [20, 20];
      case 'dotted': return [2, 20];
      case 'dash-dot': return [20, 20, 2, 30];
      default: return [];
    }
  };

  const drawWall = (ctx, wall, startCorner, endCorner, isPreview = false) => {
    if (!startCorner || !endCorner) return;
    const isSelected = wall.id === selectedWallId, wallColor = wall.color || '#000000';
    ctx.strokeStyle = isPreview ? THEME_COLOR_LIGHT : isSelected ? SELECTION_COLOR : wallColor;
    let thickness = wall.thickness !== undefined ? wall.thickness : 16;
    if (thickness <= 0 && isPreview) thickness = 2 / scale;
    ctx.lineWidth = thickness; ctx.lineCap = 'square'; ctx.lineJoin = 'miter'; ctx.globalAlpha = isPreview ? 0.6 : 1;
    const dashArray = getDashArrayForStyle(wall.lineStyle);
    ctx.setLineDash(dashArray);
    ctx.beginPath(); ctx.moveTo(startCorner.x, startCorner.y); ctx.lineTo(endCorner.x, endCorner.y);
    if (ctx.lineWidth > 0) ctx.stroke();
    ctx.setLineDash([]); ctx.globalAlpha = 1;
  };

  const drawWalls = (ctx) => {
    const groups = {};
    Object.values(walls).forEach(wall => {
      if (!wall || !wall.startCornerId || !wall.endCornerId) return;
      const isSelected = wall.id === selectedWallId, color = wall.color || '#000000', thickness = wall.thickness !== undefined ? wall.thickness : 16;
      const lineStyle = wall.lineStyle || 'solid';
      const key = `${isSelected}|${color}|${thickness}|${lineStyle}`;
      if (!groups[key]) groups[key] = []; groups[key].push(wall);
    });
    Object.entries(groups).forEach(([key, groupWalls]) => {
      const [isSelectedStr, color, thicknessStr, lineStyle] = key.split('|'), isSelected = isSelectedStr === 'true';
      let thickness = parseFloat(thicknessStr); if (thickness <= 0) { if (!isSelected) return; thickness = 2 / scale; }
      const dashArray = getDashArrayForStyle(lineStyle);
      const adjacency = {}, wallMap = {};
      groupWalls.forEach(w => {
        if (!w || !w.id || !w.startCornerId || !w.endCornerId) return;
        wallMap[w.id] = w; if (!adjacency[w.startCornerId]) adjacency[w.startCornerId] = []; if (!adjacency[w.endCornerId]) adjacency[w.endCornerId] = [];
        adjacency[w.startCornerId].push(w.id); adjacency[w.endCornerId].push(w.id);
      });
      const visited = new Set();
      groupWalls.forEach(wall => {
        if (!wall || !wall.id || visited.has(wall.id)) return;
        ctx.beginPath(); ctx.strokeStyle = isSelected ? SELECTION_COLOR : color; ctx.lineWidth = thickness; ctx.lineCap = 'square'; ctx.lineJoin = 'miter'; ctx.setLineDash(dashArray);
        const chain = [wall]; visited.add(wall.id); let currCorner = wall.startCornerId, currWall = wall;
        while (true) {
          const adj = adjacency[currCorner]; if (!adj || adj.length !== 2) break;
          const nextWallId = adj.find(id => id !== currWall.id); if (!nextWallId || visited.has(nextWallId)) break;
          const nextWall = wallMap[nextWallId]; if (!nextWall) break;
          chain.unshift(nextWall); visited.add(nextWallId); currWall = nextWall; currCorner = (nextWall.startCornerId === currCorner) ? nextWall.endCornerId : nextWall.startCornerId;
        }
        let tailCorner = wall.endCornerId, tailWall = wall;
        while(true) {
          const adj = adjacency[tailCorner]; if (!adj || adj.length !== 2) break;
          const nextWallId = adj.find(id => id !== tailWall.id); if (!nextWallId || visited.has(nextWallId)) break;
          const nextWall = wallMap[nextWallId]; if (!nextWall) break;
          chain.push(nextWall); visited.add(nextWallId); tailWall = nextWall; tailCorner = (nextWall.startCornerId === tailCorner) ? nextWall.endCornerId : nextWall.startCornerId;
        }
        if (chain.length > 0) {
          let pId = chain[0]?.startCornerId; if (!pId) return;
          if (chain.length > 1) {
            const w0 = chain[0], w1 = chain[1], shared = (w0.startCornerId === w1.startCornerId || w0.startCornerId === w1.endCornerId) ? w0.startCornerId : w0.endCornerId;
            pId = (w0.startCornerId === shared) ? w0.endCornerId : w0.startCornerId;
          }
          const startP = corners[pId]; if (!startP) return;
          ctx.moveTo(startP.x, startP.y); let prevId = pId;
          chain.forEach(w => {
            if (!w || !w.startCornerId || !w.endCornerId) return;
            const nextId = (w.startCornerId === prevId) ? w.endCornerId : w.startCornerId, np = corners[nextId];
            if (np) ctx.lineTo(np.x, np.y); prevId = nextId;
          });
          if (prevId === pId) ctx.closePath(); ctx.stroke();
        }
      });
      ctx.setLineDash([]);
    });
  };

  const drawDimension = (ctx, start, end, isPreview = false) => {
    if (!isPreview && !layers.dimensions?.isVisible) return;
    const dx = end.x - start.x, dy = end.y - start.y, len = Math.sqrt(dx * dx + dy * dy);
    if (len < 1) return;
    const ux = dx / len, uy = dy / len, nx = -uy, ny = ux, offset = 40;
    const p1 = { x: start.x + nx * offset, y: start.y + ny * offset }, p2 = { x: end.x + nx * offset, y: end.y + ny * offset };
    ctx.strokeStyle = '#555555'; ctx.fillStyle = '#555555'; ctx.lineWidth = 0.3 / scale; ctx.font = `${12 / scale}px Arial`; ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
    ctx.setLineDash([]); ctx.beginPath(); const gap = 10; ctx.moveTo(start.x + nx * gap, start.y + ny * gap); ctx.lineTo(p1.x + nx * (5 / scale), p1.y + ny * (5 / scale)); ctx.moveTo(end.x + nx * gap, end.y + ny * gap); ctx.lineTo(p2.x + nx * (5 / scale), p2.y + ny * (5 / scale)); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(p1.x, p1.y); ctx.lineTo(p2.x, p2.y); ctx.stroke();
    const tickLen = 5 / scale; ctx.beginPath(); ctx.moveTo(p1.x - (ux + nx) * tickLen, p1.y - (uy + ny) * tickLen); ctx.lineTo(p1.x + (ux + nx) * tickLen, p1.y + (uy + ny) * tickLen); ctx.moveTo(p2.x - (ux + nx) * tickLen, p2.y - (uy + ny) * tickLen); ctx.lineTo(p2.x + (ux + nx) * tickLen, p2.y + (uy + ny) * tickLen); ctx.stroke();
    const tickSize = 10 / scale; ctx.beginPath(); ctx.moveTo(p1.x - (ux + nx) * tickSize, p1.y - (uy + ny) * tickSize); ctx.lineTo(p1.x + (ux + nx) * tickSize, p1.y + (uy + ny) * tickSize); ctx.moveTo(p2.x - (ux + nx) * tickSize, p2.y - (uy + ny) * tickSize); ctx.lineTo(p2.x + (ux + nx) * tickSize, p2.y + (uy + ny) * tickSize); ctx.stroke();
    const midX = (p1.x + p2.x) / 2, midY = (p1.y + p2.y) / 2;
    ctx.save(); ctx.translate(midX, midY); let angle = Math.atan2(dy, dx); if (angle > Math.PI / 2) angle -= Math.PI; if (angle < -Math.PI / 2) angle += Math.PI; ctx.rotate(angle); ctx.fillText(`${Math.round(len)} cm`, 0, -2 / scale); ctx.restore();
  };

  const drawManualDimension = (ctx, start, end, customValue, isSelected, isPreview = false) => {
    if ((!isPreview && !layers.manualDimensions?.isVisible) || !start || !end) return;
    const dx = end.x - start.x, dy = end.y - start.y, len = Math.sqrt(dx * dx + dy * dy); if (len < 1) return;
    const angle = Math.atan2(dy, dx), ux = dx / len, uy = dy / len, nx = -uy, ny = ux;
    ctx.save(); ctx.strokeStyle = isSelected ? '#3b82f6' : '#000000'; ctx.fillStyle = isSelected ? '#3b82f6' : '#000000'; ctx.lineWidth = 1 / scale; ctx.setLineDash(isPreview ? [5 / scale, 5 / scale] : []);
    ctx.beginPath(); ctx.moveTo(start.x, start.y); ctx.lineTo(end.x, end.y); ctx.stroke();
    const tickSize = 6 / scale, tx = (ux - uy) * tickSize, ty = (uy + ux) * tickSize;
    const drawTick = (x, y) => { ctx.moveTo(x - tx, y - ty); ctx.lineTo(x + tx, y + ty); };
    ctx.beginPath(); drawTick(start.x, start.y); drawTick(end.x, end.y); ctx.lineWidth = 1.5 / scale; ctx.stroke();
    const midX = (start.x + end.x) / 2, midY = (start.y + end.y) / 2;
    let textAngle = angle; if (textAngle > Math.PI / 2) textAngle -= Math.PI; if (textAngle < -Math.PI / 2) textAngle += Math.PI;
    ctx.translate(midX, midY); ctx.rotate(textAngle); ctx.font = `${12 / scale}px Arial`; ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
    const textValue = customValue !== null && customValue !== undefined ? customValue : `${Math.round(len)} cm`;
    ctx.fillText(textValue, 0, -3 / scale); ctx.restore();
  };

  const drawBackground = (ctx) => {
    if (!backgroundItem || !layers.background?.isVisible) return;
    const img = getImage(backgroundItem.imageUrl); if (!img) return;
    const isSelected = backgroundItem.id === selectedBackgroundId; ctx.save(); ctx.globalAlpha = backgroundItem.opacity;
    const scaledWidth = backgroundItem.originalWidth * backgroundItem.scale, scaledHeight = backgroundItem.originalHeight * backgroundItem.scale;
    ctx.drawImage(img, backgroundItem.x - scaledWidth / 2, backgroundItem.y - scaledHeight / 2, scaledWidth, scaledHeight);
    ctx.globalAlpha = 1; if (isSelected) { ctx.strokeStyle = SELECTION_COLOR; ctx.lineWidth = 2 / scale; ctx.setLineDash([5 / scale, 5 / scale]); ctx.strokeRect(backgroundItem.x - scaledWidth / 2 - 5, backgroundItem.y - scaledHeight / 2 - 5, scaledWidth + 10, scaledHeight + 10); ctx.setLineDash([]); }
    ctx.restore();
  };

  const drawLogos = (ctx) => {
    if (!layers.logo?.isVisible) return;
    Object.values(logoItems).forEach(logo => {
      const img = getImage(logo.imageUrl); if (!img) return;
      const isSelected = logo.id === selectedLogoId; ctx.save(); ctx.globalAlpha = logo.opacity;
      const scaledWidth = logo.originalWidth * logo.scale, scaledHeight = logo.originalHeight * logo.scale;
      ctx.drawImage(img, logo.x - scaledWidth / 2, logo.y - scaledHeight / 2, scaledWidth, scaledHeight);
      ctx.globalAlpha = 1; if (isSelected) { ctx.strokeStyle = SELECTION_COLOR; ctx.lineWidth = 2 / scale; ctx.setLineDash([5 / scale, 5 / scale]); ctx.strokeRect(logo.x - scaledWidth / 2 - 5, logo.y - scaledHeight / 2 - 5, scaledWidth + 10, scaledHeight + 10); ctx.setLineDash([]); }
      ctx.restore();
    });
  };

  const drawTextItems = (ctx) => {
    if (!layers.texts?.isVisible) return;
    Object.values(textItems).forEach(item => {
      const isSelected = item.id === selectedTextId; ctx.save(); ctx.translate(item.x, item.y); ctx.rotate(item.rotation);
      const fontSize = item.fontSize, lineHeight = fontSize * 1.2, lines = (item.text || '').split('\n'), fontWeight = item.fontWeight || 'normal', fontFamily = item.fontFamily, textAlign = item.textAlign || 'center';
      ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`; ctx.fillStyle = item.color; ctx.textBaseline = 'middle';
      let maxWidth = 0; lines.forEach(line => { const metrics = ctx.measureText(line); if (metrics.width > maxWidth) maxWidth = metrics.width; });
      const totalHeight = lines.length * lineHeight, startY = -(totalHeight / 2) + (lineHeight / 2);
      lines.forEach((line, index) => { const y = startY + (index * lineHeight); let x = 0; if (textAlign === 'left') { x = -maxWidth / 2; ctx.textAlign = 'left'; } else if (textAlign === 'right') { x = maxWidth / 2; ctx.textAlign = 'right'; } else { x = 0; ctx.textAlign = 'center'; } ctx.fillText(line, x, y); });
      if (isSelected) { ctx.strokeStyle = SELECTION_COLOR; ctx.lineWidth = 1.5 / scale; ctx.setLineDash([4 / scale, 4 / scale]); ctx.strokeRect(-maxWidth / 2 - 5, -totalHeight / 2 - 5, maxWidth + 10, totalHeight + 10); ctx.setLineDash([]); }
      ctx.restore();
    });
  };

  const drawTables = (ctx) => {
    Object.values(tables).forEach(table => {
      const isSelected = table.id === selectedTableId; ctx.save(); ctx.translate(table.x, table.y);
      const width = table.columnWidth * 2, rowHeight = table.fontSize + table.padding * 2;
      let currentY = 0, totalHeight = 0; if (table.showTitle) totalHeight += rowHeight; totalHeight += table.data.length * rowHeight;
      ctx.fillStyle = table.backgroundColor; ctx.fillRect(0, 0, width, totalHeight); ctx.font = `${table.fontSize}px ${table.fontFamily}`; ctx.fillStyle = table.color; ctx.textBaseline = 'middle';
      if (table.showTitle) { ctx.textAlign = 'center'; if (table.showBorder) { ctx.strokeStyle = table.borderColor; ctx.lineWidth = table.borderWidth; ctx.setLineDash([]); ctx.strokeRect(0, currentY, width, rowHeight); } ctx.fillText(table.title, width / 2, currentY + rowHeight / 2); currentY += rowHeight; }
      ctx.textAlign = 'left';
      table.data.forEach((row, index) => { if (table.showBorder) { ctx.strokeStyle = table.borderColor; ctx.lineWidth = table.borderWidth; ctx.setLineDash([]); ctx.strokeRect(0, currentY, width, rowHeight); } ctx.fillText(row.label, table.padding, currentY + rowHeight / 2); ctx.fillText(row.value, table.columnWidth + table.padding, currentY + rowHeight / 2); currentY += rowHeight; });
      if (isSelected) { ctx.strokeStyle = SELECTION_COLOR; ctx.lineWidth = 2 / scale; ctx.setLineDash([5 / scale, 5 / scale]); ctx.strokeRect(-5, -5, width + 10, totalHeight + 10); ctx.setLineDash([]); }
      ctx.restore();
    });
  };

  const drawShapes = (ctx) => {
    if (!layers.shapes?.isVisible) return;
    const shapeRenderer = new ShapeRenderer(ctx, 1);
    Object.values(shapeItems).forEach(item => { const isSelected = item.id === selectedShapeId; shapeRenderer.renderItem(item, item.x, item.y, item.rotation, isSelected); });
  };

  const drawSurfaces = (ctx) => {
    Object.values(surfaces).forEach(surface => {
      if (surface.id === activeSurfaceId || surface.points.length < 3) return;
      const isSelected = surface.id === selectedSurfaceId; ctx.save(); ctx.fillStyle = surface.fillColor; ctx.globalAlpha = surface.fillOpacity;
      ctx.beginPath(); ctx.moveTo(surface.points[0].x, surface.points[0].y); for (let i = 1; i < surface.points.length; i++) ctx.lineTo(surface.points[i].x, surface.points[i].y); ctx.closePath(); ctx.fill();
      if (surface.hatchPattern && surface.hatchPattern !== HATCH_PATTERNS.NONE) { const pattern = getHatchPattern(ctx, surface.hatchPattern, surface.hatchColor || '#000000', surface.hatchScale || 1); if (pattern) { ctx.fillStyle = pattern; ctx.globalAlpha = surface.fillOpacity; ctx.fill(); } }
      ctx.globalAlpha = 1; if (isSelected) { ctx.strokeStyle = SELECTION_COLOR; ctx.lineWidth = 2 / scale; ctx.setLineDash([5 / scale, 5 / scale]); ctx.stroke(); ctx.setLineDash([]); surface.points.forEach((p, index) => { ctx.beginPath(); ctx.fillStyle = draggingSurfacePointIndex === index ? '#ea580c' : SELECTION_COLOR; const radius = (draggingSurfacePointIndex === index ? 6 : 4) / scale; ctx.arc(p.x, p.y, radius, 0, Math.PI * 2); ctx.fill(); ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 1 / scale; ctx.stroke(); }); }
      ctx.restore();
    });
  };

  const drawActiveSurface = (ctx) => {
    if (!activeSurfaceId || !surfaces[activeSurfaceId] || !tempEnd) return;
    const surface = surfaces[activeSurfaceId], points = [...surface.points, tempEnd]; ctx.save(); ctx.strokeStyle = THEME_COLOR_LIGHT; ctx.lineWidth = 2 / scale; ctx.fillStyle = surface.fillColor; ctx.globalAlpha = 0.3; ctx.beginPath(); ctx.moveTo(points[0].x, points[0].y); for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y); ctx.closePath(); ctx.fill(); ctx.globalAlpha = 1; ctx.stroke(); ctx.beginPath(); const lastPoint = surface.points[surface.points.length - 1]; ctx.moveTo(lastPoint.x, lastPoint.y); ctx.lineTo(tempEnd.x, tempEnd.y); ctx.strokeStyle = THEME_COLOR_LIGHT; ctx.setLineDash([5 / scale, 5 / scale]); ctx.stroke(); ctx.setLineDash([]); ctx.fillStyle = THEME_COLOR_LIGHT; surface.points.forEach(p => { ctx.beginPath(); ctx.arc(p.x, p.y, 4 / scale, 0, Math.PI * 2); ctx.fill(); }); ctx.restore();
  };

  const drawRoomPreview = (ctx) => {
    if (tool === TOOLS.ROOM && drawing && drawingStartPos && tempEnd) {
      const startX = drawingStartPos.x, startY = drawingStartPos.y, width = tempEnd.x - startX, height = tempEnd.y - startY;
      ctx.save(); ctx.strokeStyle = THEME_COLOR_LIGHT; ctx.lineWidth = 20 / scale; ctx.fillStyle = '#ffffff'; ctx.globalAlpha = 0.5; ctx.setLineDash([]); ctx.beginPath(); ctx.rect(startX, startY, width, height); ctx.fill(); ctx.globalAlpha = 0.6; ctx.stroke(); ctx.globalAlpha = 1; ctx.fillStyle = SELECTION_COLOR; ctx.font = `bold ${14 / scale}px Arial`; ctx.textAlign = 'center'; ctx.textBaseline = 'bottom'; ctx.fillText(`${Math.abs(Math.round(width))}cm`, startX + width/2, startY - 5/scale); ctx.textAlign = 'left'; ctx.textBaseline = 'middle'; ctx.fillText(`${Math.abs(Math.round(height))}cm`, startX + width + 5/scale, startY + height/2); ctx.restore();
    }
  };

  const drawGrid = (ctx) => {
    if (!layers.grid?.isVisible) return;
    const startX = Math.floor((-position.x / scale) / GRID_SIZE) * GRID_SIZE, startY = Math.floor((-position.y / scale) / GRID_SIZE) * GRID_SIZE, endX = startX + (canvasSize.width / scale) + GRID_SIZE, endY = startY + (canvasSize.height / scale) + GRID_SIZE;
    ctx.beginPath(); ctx.strokeStyle = '#e5e7eb'; ctx.lineWidth = 1 / scale; ctx.globalAlpha = 0.5; ctx.setLineDash([]);
    for (let x = startX; x <= endX; x += GRID_SIZE) { if (Math.round(x / GRID_SIZE) % 5 === 0) continue; ctx.moveTo(x, startY); ctx.lineTo(x, endY); }
    for (let y = startY; y <= endY; y += GRID_SIZE) { if (Math.round(y / GRID_SIZE) % 5 === 0) continue; ctx.moveTo(startX, y); ctx.lineTo(endX, y); }
    ctx.stroke(); ctx.beginPath(); ctx.strokeStyle = '#e5e7eb'; ctx.lineWidth = 2 / scale; ctx.globalAlpha = 1.0;
    for (let x = startX; x <= endX; x += GRID_SIZE) { if (Math.round(x / GRID_SIZE) % 5 !== 0) continue; ctx.moveTo(x, startY); ctx.lineTo(x, endY); }
    for (let y = startY; y <= endY; y += GRID_SIZE) { if (Math.round(y / GRID_SIZE) % 5 !== 0) continue; ctx.moveTo(startX, y); ctx.lineTo(endX, y); }
    ctx.stroke(); ctx.globalAlpha = 1.0;
  };

  const drawRooms = (ctx) => {
    Object.values(rooms).forEach(room => {
      const roomCorners = room.corners.map(cornerId => corners[cornerId]).filter(Boolean); if (roomCorners.length < 3) return;
      let cleanFillColor = room.fillColor || '#ffffff'; if (cleanFillColor.length > 7 && cleanFillColor.startsWith('#')) cleanFillColor = cleanFillColor.substring(0, 7);
      const fillOpacity = (room.fillOpacity !== undefined ? room.fillOpacity : 50) / 100, isSelected = room.id === selectedRoomId;
      ctx.fillStyle = cleanFillColor + Math.round(fillOpacity * 255).toString(16).padStart(2, '0'); ctx.strokeStyle = isSelected ? SELECTION_COLOR : 'rgba(255,255,255,0)'; ctx.lineWidth = isSelected ? 3 / scale : 2 / scale; ctx.lineJoin = 'miter'; ctx.setLineDash([]); ctx.beginPath(); ctx.moveTo(roomCorners[0].x, roomCorners[0].y); for (let i = 1; i < roomCorners.length; i++) ctx.lineTo(roomCorners[i].x, roomCorners[i].y); ctx.closePath(); ctx.fill();
      if (room.hatchPattern && room.hatchPattern !== HATCH_PATTERNS.NONE) { ctx.save(); const pattern = getHatchPattern(ctx, room.hatchPattern, room.hatchColor || '#000000', room.hatchScale || 1); if (pattern) { ctx.fillStyle = pattern; ctx.globalAlpha = fillOpacity; ctx.fill(); } ctx.restore(); }
      ctx.stroke();
      if (layers.roomLabels?.isVisible) { ctx.fillStyle = '#1f2937'; ctx.font = `bold ${14 / scale}px Arial`; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; const nameText = room.name || `Pomieszczenie ${room.id.substring(0, 4)}`, areaValue = (room.manualArea !== undefined && room.manualArea !== null) ? parseFloat(room.manualArea) : room.area, areaText = `${areaValue.toFixed(2)} m²`; ctx.fillText(nameText, room.center.x, room.center.y - 10 / scale); ctx.font = `${12 / scale}px Arial`; ctx.fillText(areaText, room.center.x, room.center.y + 10 / scale); }
    });
  };

  const drawElement = (ctx, element) => {
    const isSelected = element.id === selectedElementId;
    const isDoor = element.type === ELEMENT_TYPES.DOOR;
    const isDoubleDoor = element.type === ELEMENT_TYPES.DOUBLE_DOOR;
    const isSlidingDoor = element.type === ELEMENT_TYPES.SLIDING_DOOR;
    const isOpening = element.type === ELEMENT_TYPES.OPENING;
    const isGarageDoor = element.type === ELEMENT_TYPES.GARAGE_DOOR;
    ctx.save(); ctx.translate(element.position.x, element.position.y); ctx.rotate(element.angle); 
    if (isDoor || isDoubleDoor) { const scaleX = element.flipX ? -1 : 1, scaleY = element.flipY ? -1 : 1; ctx.scale(scaleX, scaleY); }
    const strokeColor = isSelected ? SELECTION_COLOR : (element.strokeColor || '#000000'), fillColor = element.fillColor || '#ffffff'; 
    ctx.strokeStyle = strokeColor; ctx.fillStyle = fillColor; ctx.lineWidth = 1 / scale; ctx.setLineDash([]);
    const width = element.width, depth = element.depth || 15;
    ctx.beginPath(); ctx.rect(-width / 2, -depth / 2, width, depth); ctx.fill();
    if (isDoor) { ctx.beginPath(); ctx.moveTo(-width / 2, depth / 2); ctx.arc(-width / 2, depth / 2, width, 0, -Math.PI / 2, true); ctx.stroke(); ctx.beginPath(); ctx.rect(-width / 2, -width + depth / 2, 4, width); ctx.fill(); ctx.stroke(); ctx.fillStyle = '#ffffff'; ctx.fillRect(-width / 2, -depth / 2, 4, depth); ctx.fillRect(width / 2 - 2, -depth / 2, 2, depth); }
    else if (isDoubleDoor) { ctx.beginPath(); ctx.moveTo(-width / 2, depth / 2); ctx.arc(-width / 2, depth / 2, width / 2, 0, -Math.PI / 2, true); ctx.stroke(); ctx.beginPath(); ctx.rect(-width / 2, -width / 2 + depth / 2, 4, width / 2); ctx.fill(); ctx.stroke(); ctx.beginPath(); ctx.moveTo(width / 2, depth / 2); ctx.arc(width / 2, depth / 2, width / 2, Math.PI, -Math.PI / 2, false); ctx.stroke(); ctx.beginPath(); ctx.rect(width / 2 - 4, -width / 2 + depth / 2, 4, width / 2); ctx.fill(); ctx.stroke(); ctx.fillStyle = '#ffffff'; ctx.fillRect(-width / 2, -depth / 2, 4, depth); ctx.fillRect(width / 2 - 4, -depth / 2, 4, depth); }
    else if (isSlidingDoor) { const panelThickness = 5; const overlap = 10; const panelWidth = (width / 2) + overlap; ctx.fillStyle = fillColor; ctx.strokeStyle = strokeColor; ctx.lineWidth = 1 / scale; ctx.beginPath(); ctx.rect(-width / 2, -panelThickness, panelWidth, panelThickness); ctx.fill(); ctx.stroke(); ctx.beginPath(); ctx.rect(width / 2 - panelWidth, 0, panelWidth, panelThickness); ctx.fill(); ctx.stroke(); }
    else if (isOpening) { }
    else if (isGarageDoor) { ctx.strokeRect(-width / 2, -depth / 2, width, depth); ctx.lineWidth = 4 / scale; ctx.beginPath(); ctx.moveTo(-width / 2, -depth / 2 + 3); ctx.lineTo(width / 2, -depth / 2 + 3); ctx.lineWidth = 1 / scale; ctx.beginPath(); ctx.moveTo(-width / 2, depth / 2 - 4); ctx.lineTo(width / 2, depth / 2 - 4); ctx.stroke(); }
    else { ctx.strokeRect(-width / 2, -depth / 2, width, depth); ctx.beginPath(); ctx.moveTo(-width / 2, -2); ctx.lineTo(width / 2, -2); ctx.moveTo(-width / 2, 2); ctx.lineTo(width / 2, 2); ctx.stroke(); }
    if (isSelected) { ctx.strokeStyle = SELECTION_COLOR; ctx.lineWidth = 2 / scale; ctx.setLineDash([5 / scale, 5 / scale]); let selHeight = depth; if (isDoor) selHeight = width + depth; if (isDoubleDoor) selHeight = width / 2 + depth; let selY = -depth / 2; if (isDoor) selY = -width + depth / 2; if (isDoubleDoor) selY = -width / 2 + depth / 2; ctx.strokeRect(-width / 2 - 5, selY - 5, width + 10, selHeight + 10); ctx.setLineDash([]); }
    ctx.restore();
  };

  const drawCorners = (ctx) => {
    Object.entries(corners).forEach(([id, corner]) => {
      // ✅ Ulepszona widoczność podczas rysowania i selekcji
      let isVisible = false; 
      if (tool === TOOLS.WALL || tool === TOOLS.ROOM || tool === TOOLS.NODE) { 
        isVisible = true; 
      } else if (tool === TOOLS.SELECT || tool === TOOLS.LAYERS || tool === 'SELECT') { 
        if (selectedCornerId === id) isVisible = true; 
        else if (selectedWallId && walls[selectedWallId]) { 
          const wall = walls[selectedWallId]; 
          if (wall.startCornerId === id || wall.endCornerId === id) isVisible = true; 
        } 
      }
      
      if (!isVisible) return;
      
      const isSelected = id === selectedCornerId;
      const isDraggingThis = isDraggingCorner && selectedCornerId === id;
      
      // ✅ Obszar z transparentnym kolorem #7fa8e1 (Halo)
      ctx.save();
      ctx.beginPath();
      const haloRadius = 12 / scale;
      ctx.fillStyle = '#7fa8e1';
      ctx.globalAlpha = 0.35;
      ctx.arc(corner.x, corner.y, haloRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // ✅ Główny punkt węzłowy
      const size = (isSelected ? 5 : 4) / scale;
      ctx.beginPath();
      ctx.arc(corner.x, corner.y, size, 0, Math.PI * 2);
      ctx.fillStyle = isSelected ? SELECTION_COLOR : '#4b5563';
      ctx.fill();
      
      // Obramowanie punktu
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.5 / scale;
      ctx.stroke();
    });
  };

  const getSnappedPos = (currentPos, originPos) => {
    if (!originPos) return currentPos; const dx = currentPos.x - originPos.x, dy = currentPos.y - originPos.y, dist = Math.sqrt(dx * dx + dy * dy); if (dist < 10 / scale) return currentPos;
    const angleRad = Math.atan2(dy, dx); let angleDeg = angleRad * (180 / Math.PI); if (angleDeg < 0) angleDeg += 360;
    const snapAngleThreshold = 2; if (angleDeg < snapAngleThreshold || angleDeg > 360 - snapAngleThreshold || Math.abs(angleDeg - 180) < snapAngleThreshold) return { x: currentPos.x, y: originPos.y };
    if (Math.abs(angleDeg - 90) < snapAngleThreshold || Math.abs(angleDeg - 270) < snapAngleThreshold) return { x: originPos.x, y: currentPos.y };
    return currentPos;
  };

  const drawAlignmentGuides = (ctx) => {
    const isDragging = isDraggingFurniture || isDraggingWall || isDraggingElement || isDraggingCorner || isDraggingShape || isDraggingText || isDraggingTable || isDraggingLogo || isDraggingSurface;
    if (!isDragging || !dragOrigin.current) return;
    const start = dragOrigin.current; let current = screenToWorld(lastMousePos.x, lastMousePos.y); current = getSnappedPos(current, start);
    ctx.save(); ctx.strokeStyle = 'rgba(59, 130, 246, 0.5)'; ctx.lineWidth = 1 / scale; ctx.setLineDash([5 / scale, 5 / scale]); ctx.beginPath(); ctx.moveTo(start.x, -100000); ctx.lineTo(start.x, 100000); ctx.stroke(); ctx.beginPath(); ctx.moveTo(-100000, start.y); ctx.lineTo(100000, start.y); ctx.stroke();
    const dx = current.x - start.x, dy = current.y - start.y, dist = Math.sqrt(dx*dx + dy*dy);
    if (dist > 10 / scale) { ctx.beginPath(); ctx.moveTo(start.x, start.y); ctx.lineTo(current.x, current.y); const isAligned = Math.abs(dx) < 0.01 || Math.abs(dy) < 0.01; ctx.strokeStyle = isAligned ? '#3b82f6' : '#f59e0b'; ctx.setLineDash([]); ctx.stroke(); let angle = Math.atan2(dy, dx) * (180 / Math.PI); if (angle < 0) angle += 360; angle = Math.round(angle); const isOrthogonal = angle % 90 === 0; ctx.fillStyle = isOrthogonal ? '#3b82f6' : '#6b7280'; ctx.font = `bold ${12 / scale}px Arial`; ctx.textAlign = 'left'; ctx.textBaseline = 'bottom'; ctx.fillText(`${angle}°`, current.x + 10/scale, current.y - 10/scale); }
    ctx.restore();
  };

  const drawFurniture = (ctx) => {
    if (layers.furniture?.isVisible) {
      const furnitureRenderer = new FurnitureRenderer(ctx, 1);
      Object.values(furnitureItems).forEach((item) => {
        const template = getItemById(item.itemId);
        if (template) {
          const currentWidth = item.width !== undefined ? item.width : (template.dimensions.width * item.scale), currentDepth = item.depth !== undefined ? item.depth : (template.dimensions.depth * item.scale), scaleX = currentWidth / template.dimensions.width, scaleY = currentDepth / template.dimensions.depth, fullItem = { ...template, dimensions: template.dimensions, ...item, scaleX, scaleY }, isSelected = item.id === selectedFurnitureId;
          furnitureRenderer.renderItem(fullItem, item.x, item.y, item.rotation, isSelected);
        }
      });
    }
  };

  const drawWatermark = (ctx) => {
    if (planId !== 'FREE') return;
    ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0); const text = 'Usuń watermark – przejdź na PRO'; ctx.font = 'bold 20px Arial'; ctx.fillStyle = 'rgba(0, 0, 0, 0.3)'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    const angle = -Math.PI / 4, spacingX = 300, spacingY = 300, diag = Math.sqrt(canvasSize.width * canvasSize.width + canvasSize.height * canvasSize.height), startX = -diag, endX = diag + spacingX, startY = -diag, endY = diag + spacingY;
    for (let x = startX; x < endX; x += spacingX) { for (let y = startY; y < endY; y += spacingY) { ctx.save(); ctx.translate(x, y); ctx.rotate(angle); ctx.fillText(text, 0, 0); ctx.restore(); } }
    ctx.restore();
  };

  const render = () => {
    const ctx = canvasRef.current?.getContext('2d'); if (!ctx) return;
    ctx.clearRect(0, 0, canvasSize.width, canvasSize.height); ctx.save(); ctx.translate(position.x, position.y); ctx.scale(scale, scale);
    drawBackground(ctx); drawGrid(ctx); drawRooms(ctx); drawSurfaces(ctx); drawActiveSurface(ctx); drawWalls(ctx);
    Object.values(walls).forEach(wall => { if (wall && wall.endCornerId) { const start = corners[wall.startCornerId], end = corners[wall.endCornerId]; if(start && end) drawDimension(ctx, start, end); } });
    if (layers.manualDimensions?.isVisible) Object.values(dimensionLines).forEach(dim => drawManualDimension(ctx, dim.start, dim.end, dim.customValue, dim.id === selectedDimensionId));
    if (drawing && tool === TOOLS.DIMENSION_LINE && drawingStartPos && tempEnd) drawManualDimension(ctx, drawingStartPos, tempEnd, null, false, true);
    drawRoomPreview(ctx);
    if (drawing && activeWallId && walls[activeWallId] && tempEnd && tool === TOOLS.WALL) { const wall = walls[activeWallId], start = corners[wall.startCornerId]; if (start) { drawWall(ctx, wall, start, tempEnd, true); drawDimension(ctx, start, tempEnd, true); } }
    Object.values(elements).forEach((element) => drawElement(ctx, element)); drawShapes(ctx); drawFurniture(ctx); drawTextItems(ctx); drawTables(ctx); drawLogos(ctx);
    
    // ✅ Rysowanie węzłów/punktów z lepszą widocznością
    if (tool === TOOLS.SELECT || tool === TOOLS.WALL || tool === TOOLS.ROOM || tool === TOOLS.LAYERS || tool === 'SELECT' || tool === TOOLS.NODE) drawCorners(ctx);
    
    drawAlignmentGuides(ctx); ctx.restore(); drawWatermark(ctx);
  };

  const handleMouseDown = (e) => {
    const worldPos = screenToWorld(e.clientX, e.clientY), currentTime = Date.now(), ctx = canvasRef.current.getContext('2d'); dragOrigin.current = worldPos;
    if (e.button === 1) { setIsPanning(true); setLastMousePos({ x: e.clientX, y: e.clientY }); return; }
    if (tool === TOOLS.FURNITURE || tool === TOOLS.STAIRS || tool === TOOLS.NORTH_ARROW) {
      const furniture = findFurnitureAtPosition(worldPos);
      if (furniture) { selectFurnitureItem(furniture.id); startDraggingFurniture(furniture.id, worldPos); clearSelection(); } else clearFurnitureSelection();
      return;
    }
    if (tool === TOOLS.SHAPE) { const shape = findShapeAtPosition(worldPos); if (shape) { selectShapeItem(shape.id); startDraggingShape(shape.id, worldPos); clearSelection(); } return; }
    if (tool === TOOLS.TEXT) { const text = findTextAtPosition(worldPos, ctx); if (text) { selectTextItem(text.id); startDraggingText(text.id, worldPos); clearSelection(); } else { addTextItem(worldPos, {}, false); setTool(TOOLS.SELECT); } return; }
    if (tool === TOOLS.TABLE) { const table = findTableAtPosition(worldPos, ctx); if (table) { if (isDoubleClick(currentTime)) startEditingTable(table.id); else { selectTable(table.id); startDraggingTable(table.id, worldPos); } clearSelection(); return; } }
    if (tool === TOOLS.SURFACE) { if (!drawing) startDrawingSurface(worldPos); else isDoubleClick(currentTime) ? finishDrawingSurface() : addPointToSurface(worldPos); return; }
    if (tool === TOOLS.ROOM) { const snap = getSnapPoint(worldPos, corners, SNAP_THRESHOLD / scale, GRID_SIZE, walls), finalPos = { x: snap.x, y: snap.y }; !drawing ? startDrawingRoom(finalPos) : finishDrawingRoom(finalPos); return; }
    if (tool === TOOLS.DIMENSION_LINE) { const snap = getSnapPoint(worldPos, corners, SNAP_THRESHOLD / scale, GRID_SIZE, walls), finalPos = { x: snap.x, y: snap.y }; if (!drawing) startDrawingDimension(finalPos); else finishDrawingDimension(finalPos); return; }
    if (tool === TOOLS.NODE) { const closestWall = getClosestWall(worldPos, walls, corners, 20); if (closestWall) addNodeToWall(closestWall.wall.id, closestWall.snapInfo.point); return; }
    if (tool === TOOLS.WALL) {
      const snap = getSnapPoint(worldPos, corners, SNAP_THRESHOLD / scale, GRID_SIZE, walls), finalPos = { x: snap.x, y: snap.y };
      if (!drawing) startDrawing(finalPos, snap.snappedTo === 'corner' ? snap.id : null);
      else if (isDoubleClick(currentTime) && snap.id === walls[activeWallId].startCornerId) finishDrawing(finalPos, snap.id);
      else finishDrawing(finalPos, snap.snappedTo === 'corner' ? snap.id : null);
    } 
    else if (tool === ELEMENT_TYPES.DOOR || tool === ELEMENT_TYPES.WINDOW || tool === ELEMENT_TYPES.DOUBLE_DOOR || tool === ELEMENT_TYPES.SLIDING_DOOR || tool === ELEMENT_TYPES.OPENING || tool === ELEMENT_TYPES.GARAGE_DOOR) {
      addElement(tool, worldPos);
    }
    else if (tool === TOOLS.SELECT || tool === 'LOGO' || tool === 'BACKGROUND' || tool === TOOLS.LAYERS || tool === 'SELECT') {
      if (selectedSurfaceId && surfaces[selectedSurfaceId]) { const vertexIndex = findSurfaceVertexAtPosition(worldPos, surfaces[selectedSurfaceId]); if (vertexIndex !== -1) { startDraggingSurfacePoint(selectedSurfaceId, vertexIndex); return; } }
      const object = findObjectAtPosition(worldPos, ctx); clearSelection(); clearFurnitureSelection(); clearLogoSelection(); clearBackgroundSelection(); clearTextSelection(); clearTableSelection(); clearShapeSelection();
      if (object) {
        switch (object.type) {
          case 'dimension': selectDimension(object.id); break;
          case 'shape': selectShapeItem(object.id); startDraggingShape(object.id, worldPos); break;
          case 'table': isDoubleClick(currentTime) ? startEditingTable(object.id) : (selectTable(object.id), startDraggingTable(object.id, worldPos)); break;
          case 'text': isDoubleClick(currentTime) ? startEditingText(object.id) : (selectTextItem(object.id), startDraggingText(object.id, worldPos)); break;
          case 'background': selectBackgroundItem(object.id); startDraggingBackground(worldPos); break;
          case 'logo': selectLogoItem(object.id); startDraggingLogo(object.id, worldPos); break;
          case 'room': selectRoom(object.id); break;
          case 'furniture': selectFurnitureItem(object.id); startDraggingFurniture(object.id, worldPos); break;
          case 'element': selectElement(object.id); startDraggingElement(object.id); break;
          case 'corner': selectCorner(object.id); startDraggingCorner(object.id); break;
          case 'wall': if (isDoubleClick(currentTime)) { const closestWallDouble = getClosestWall(worldPos, walls, corners, 20); if (closestWallDouble && closestWallDouble.wall.id === object.id) { addNodeToWall(object.id, closestWallDouble.snapInfo.point); return; } } selectWall(object.id); startDraggingWall(object.id); break;
          case 'surface': selectSurface(object.id); startDraggingSurfaceAction(object.id, worldPos); break;
        }
      } else if (tool === TOOLS.SELECT || tool === 'SELECT') { setIsPanning(true); setLastMousePos({ x: e.clientX, y: e.clientY }); }
    }
  };

  const handleMouseMove = (e) => {
    const currentMousePos = { x: e.clientX, y: e.clientY };
    if (isPanning) { const deltaX = e.clientX - lastMousePos.x, deltaY = e.clientY - lastMousePos.y; setStagePosition({ x: position.x + deltaX, y: position.y + deltaY }); setLastMousePos(currentMousePos); return; }
    const worldPos = screenToWorld(e.clientX, e.clientY);
    if (isDraggingShape && selectedShapeId) { const angleSnapped = getSnappedPos(worldPos, dragOrigin.current), snappedPos = { x: Math.round(angleSnapped.x / 5) * 5, y: Math.round(angleSnapped.y / 5) * 5 }; updateDraggingShape(snappedPos); return; }
    if (isDraggingTable && selectedTableId) { const angleSnapped = getSnappedPos(worldPos, dragOrigin.current); updateDraggingTable(angleSnapped); return; }
    if (isDraggingText && selectedTextId) { const angleSnapped = getSnappedPos(worldPos, dragOrigin.current); updateDraggingText(angleSnapped); return; }
    if (isDraggingBackground && backgroundItem) { const angleSnapped = getSnappedPos(worldPos, dragOrigin.current); updateDraggingBackground(angleSnapped); return; }
    if (isDraggingLogo && selectedLogoId) { const angleSnapped = getSnappedPos(worldPos, dragOrigin.current); updateDraggingLogo(angleSnapped); return; }
    if (isDraggingFurniture && selectedFurnitureId) { const angleSnapped = getSnappedPos(worldPos, dragOrigin.current), snappedPos = { x: Math.round(angleSnapped.x / 5) * 5, y: Math.round(angleSnapped.y / 5) * 5 }; updateDraggingFurniture(snappedPos); return; }
    if (draggingSurfacePointIndex !== null && selectedSurfaceId) { const angleSnapped = getSnappedPos(worldPos, dragOrigin.current), snap = getSnapPoint(angleSnapped, corners, SNAP_THRESHOLD / scale, GRID_SIZE, walls); updateDraggingSurfacePoint({ x: snap.x, y: snap.y }); return; }
    if (isDraggingSurface && selectedSurfaceId) { const prevWorldPos = screenToWorld(lastMousePos.x, lastMousePos.y), prevSnapped = getSnappedPos(prevWorldPos, dragOrigin.current), currSnapped = getSnappedPos(worldPos, dragOrigin.current), deltaX = currSnapped.x - prevSnapped.x, deltaY = currSnapped.y - prevSnapped.y; updateDraggingSurface(deltaX, deltaY); setLastMousePos(currentMousePos); return; }
    if (isDraggingCorner && selectedCornerId) { const angleSnapped = getSnappedPos(worldPos, dragOrigin.current), snap = getSnapPoint(angleSnapped, corners, SNAP_THRESHOLD / scale, GRID_SIZE, walls); updateDraggingCorner({ x: snap.x, y: snap.y }); }
    else if (isDraggingElement && selectedElementId) updateDraggingElement(worldPos);
    else if (isDraggingWall && selectedWallId) { const prevWorldPos = screenToWorld(lastMousePos.x, lastMousePos.y), prevSnapped = getSnappedPos(prevWorldPos, dragOrigin.current), currSnapped = getSnappedPos(worldPos, dragOrigin.current), deltaX = currSnapped.x - prevSnapped.x, deltaY = currSnapped.y - prevSnapped.y; updateDraggingWall(deltaX, deltaY); setLastMousePos(currentMousePos); return; }
    if (drawing) {
      if (tool === TOOLS.WALL) { const snap = getSnapPoint(worldPos, corners, SNAP_THRESHOLD / scale, GRID_SIZE, walls); updateDrawing({ x: snap.x, y: snap.y }); }
      else if (tool === TOOLS.ROOM) { const snap = getSnapPoint(worldPos, corners, SNAP_THRESHOLD / scale, GRID_SIZE, walls); updateDrawingRoom({ x: snap.x, y: snap.y }); }
      else if (tool === TOOLS.SURFACE) updateDrawing({ x: worldPos.x, y: worldPos.y });
      else if (tool === TOOLS.DIMENSION_LINE) { const snap = getSnapPoint(worldPos, corners, SNAP_THRESHOLD / scale, GRID_SIZE, walls); updateDrawingDimension({ x: snap.x, y: snap.y }); }
    }
    setLastMousePos(currentMousePos);
  };

  const handleMouseUp = () => { stopDragging(); stopDraggingFurniture(); stopDraggingLogo(); stopDraggingBackground(); stopDraggingText(); stopDraggingTable(); stopDraggingShape(); setIsPanning(false); dragOrigin.current = null; };
  const handleWheel = (e) => { e.preventDefault(); const scaleBy = 1.1, worldPos = screenToWorld(e.clientX, e.clientY), newScale = e.deltaY > 0 ? scale / scaleBy : scale * scaleBy; setStageScale(newScale); const newWorldPos = screenToWorld(e.clientX, e.clientY), deltaX = (newWorldPos.x - worldPos.x) * newScale, deltaY = (newWorldPos.y - worldPos.y) * newScale; setStagePosition({ x: position.x + deltaX, y: position.y + deltaY }); };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; };

  const handleDrop = (e) => {
    e.preventDefault();
    const furnitureItemId = e.dataTransfer.getData('furnitureItemId'); if (furnitureItemId) { const worldPos = screenToWorld(e.clientX, e.clientY), id = addFurnitureItem(furnitureItemId, worldPos); if (id) { selectFurnitureItem(id); setTool(TOOLS.FURNITURE); } return; }
    const stairItemId = e.dataTransfer.getData('stairItemId'); if (stairItemId) { const worldPos = screenToWorld(e.clientX, e.clientY), id = addFurnitureItem(stairItemId, worldPos); if (id) { selectFurnitureItem(id); setTool(TOOLS.STAIRS); } return; }
    const compassItemId = e.dataTransfer.getData('compassItemId'); if (compassItemId) { const worldPos = screenToWorld(e.clientX, e.clientY), id = addFurnitureItem(compassItemId, worldPos); if (id) { selectFurnitureItem(id); setTool(TOOLS.NORTH_ARROW); } return; }
    const shapeItemId = e.dataTransfer.getData('shapeItemId'); if (shapeItemId) { const worldPos = screenToWorld(e.clientX, e.clientY), id = addShapeItem(shapeItemId, worldPos); if (id) { selectShapeItem(id); setTool(TOOLS.SHAPE); } }
  };

  useEffect(() => { render(); }, [canvasSize, scale, position, corners, walls, elements, rooms, furnitureItems, logoItems, backgroundItem, surfaces, textItems, tables, shapeItems, dimensionLines, activeWallId, activeSurfaceId, tempEnd, drawing, selectedCornerId, selectedWallId, selectedElementId, selectedRoomId, selectedFurnitureId, selectedLogoId, selectedBackgroundId, selectedSurfaceId, selectedTextId, selectedTableId, selectedShapeId, selectedDimensionId, draggingSurfacePointIndex, lastMousePos, forceUpdate, layers, planId]);

  const getCursorStyle = () => { if (isPanning || draggingSurfacePointIndex !== null) return 'grabbing'; if (tool === TOOLS.SELECT || tool === TOOLS.LAYERS || tool === 'SELECT') return 'default'; if (tool === TOOLS.TEXT) return 'text'; if (tool === TOOLS.NODE || tool === TOOLS.DIMENSION_LINE) return 'crosshair'; return 'crosshair'; };

  return (
    <div className="w-full h-full bg-white overflow-hidden relative">
      <canvas ref={canvasRef} id="main-canvas" width={canvasSize.width} height={canvasSize.height} className="absolute inset-0" onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} onWheel={handleWheel} onContextMenu={(e) => e.preventDefault()} onDragOver={handleDragOver} onDrop={handleDrop} style={{ cursor: getCursorStyle() }} />
      <div className="absolute top-4 right-4 flex flex-col gap-2 z-40">
        <button onClick={togglePanel} className={`relative p-2 rounded-xl shadow-lg transition-all border-2 ${isPanelOpen ? "bg-[#1E2937] text-white border-[#1E2937]" : "bg-white text-gray-600 border-gray-100 hover:border-gray-300 hover:bg-gray-50"}`} title="Zarządzaj Piętrami">
          <SafeIcon icon={RiHome9Line} className="text-xl" />
          <div className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-[#1E2937] text-white text-[9px] font-bold rounded-full border-2 border-white flex items-center justify-center shadow-sm">{floors.length}</div>
        </button>
        <button onClick={() => setTool(tool === TOOLS.LAYERS ? TOOLS.SELECT : TOOLS.LAYERS)} className={`p-2 rounded-xl shadow-lg transition-all border-2 ${tool === TOOLS.LAYERS ? "bg-[#1E2937] text-white border-[#1E2937]" : "bg-white text-gray-600 border-gray-100 hover:border-gray-300 hover:bg-gray-50"}`} title="Warstwy">
          <SafeIcon icon={RiListSettingsLine} className="text-xl" />
        </button>
        <button onClick={() => setIsHistoryOpen(true)} className={`p-2 rounded-xl shadow-lg transition-all border-2 bg-white text-gray-600 border-gray-100 hover:border-gray-300 hover:bg-gray-50`} title="Historia rzutu">
          <SafeIcon icon={RiHistoryLine} className="text-xl" />
        </button>
      </div>
      <FloorsPanel />
      <LayersPanel />
      <HistoryPanel isOpen={isHistoryOpen} onClose={() => setIsHistoryOpen(false)} />
      <div className="absolute bottom-4 right-4 bg-white/80 p-2.5 rounded-xl text-xs text-gray-600 backdrop-blur-sm z-20 border border-gray-200 shadow-sm flex items-center gap-3">
        <div className="flex items-center gap-1.5">
          <button onClick={() => { setStageScale(1); setStagePosition({ x: 0, y: 0 }); }} className="p-1 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-600" title="Resetuj widok (Zoom 100%, Pos 0,0)">
            <SafeIcon icon={RiDragMoveFill} className="text-sm" />
          </button>
          <span>Zoom: {Math.round(scale * 100)}%</span>
        </div>
        <div className="w-px h-3 bg-gray-300" />
        <span>Pos: {Math.round(position.x)}, {Math.round(position.y)}</span>
        {(tool !== TOOLS.SELECT && tool !== 'SELECT') && (
          <>
            <div className="w-px h-3 bg-gray-300" />
            <span className="text-[#1E2937] font-medium">
              {tool === TOOLS.FURNITURE && "Tryb Aranżacji"}
              {tool === TOOLS.STAIRS && "Tryb Schodów"}
              {tool === TOOLS.NORTH_ARROW && "Róża Wiatrów"}
              {tool === TOOLS.SURFACE && "Rysowanie Powierzchni"}
              {tool === TOOLS.ROOM && "Rysowanie Pomieszczenia"}
              {tool === TOOLS.TEXT && "Wstawianie Tekstu"}
              {tool === TOOLS.TABLE && "Metryczka"}
              {tool === TOOLS.SHAPE && "Kształty"}
              {tool === TOOLS.NODE && "Dodaj Węzeł"}
              {tool === TOOLS.DIMENSION_LINE && "Rysowanie Wymiaru"}
              {tool === TOOLS.LAYERS && "Zarządzanie Warstwami"}
              {tool === ELEMENT_TYPES.GARAGE_DOOR && "Wstawianie Bramy"}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

export default Canvas2D;